import cubejs, { ResultSet } from '@cubejs-client/core';
import { CubeProvider } from '@cubejs-client/react';
import { startCase } from 'lodash-es';
import React, { ReactElement, ReactNode, useMemo, useRef } from 'react';
import { UseTranslationResponse } from 'react-i18next';
import { useHttp } from './network';

export type CubeResults = {
  data: ResultSet | undefined;
  isLoading: boolean;
};

export function CubeClient({
  apiEndpoint,
  tokenEndpoint,
  children,
  clientId
}: {
  apiEndpoint: string;
  tokenEndpoint: string | undefined;
  children: ReactNode;
  clientId?: string | undefined;
}): ReactElement | null {
  const { get } = useHttp();
  const tokenRef = useRef<string | null>(null);

  const cubejsApi = useMemo(() => {
    tokenRef.current = null;
    //suspenseCache.current = {}; // reset cache on API change
    return cubejs('bearer', {
      apiUrl: `${apiEndpoint}/command`,
      ...(clientId ? { headers: { 'x-client-id': clientId } } : {})
    });
  }, [apiEndpoint, tokenEndpoint, tokenRef, get, clientId]);

  return (
    <CubeProvider
      {...{ cubejsApi: cubejsApi, headers: { 'x-client-id': clientId } }}
      cubeApi={cubejsApi}
    >
      {children}
    </CubeProvider>
  );
}

export function getCubeMemberTitle(
  name: string,
  t: UseTranslationResponse<'translation', null>['t']
): string {
  //console.log(name);
  if (!name || !name.includes('.'))
    return t(`labels.${name}`).startsWith('labels.') ? name : t(`labels.${name}`);
  if (name.includes(','))
    return name
      .split(',')
      .map(subName => getCubeMemberTitle(subName, t))
      .join(', '); // multiple label levels
  return t('labels.' + name.split('.')[1], startCase(name.split('.')[1].replace(/_/g, ' ')));
}

export function getCubeMemberSeriesName(
  name: string,
  t: UseTranslationResponse<'translation', null>['t']
): string {
  return t(`reports:${name.split(',').join('.')}`);
}

export function getPriorityColors(priorty: string): string {
  switch (priorty) {
    case 'P1':
      return '#800020';
    case 'P2':
      return '#FF5D00';
    case 'P3':
      return '#ffc104';
    default:
      // default color, ie P4
      return '#4572c5';
  }
}
